import _ from 'lodash';
import { GetCollectionsQuery, AccessType } from '../../../../generated/graphql';
import { SalesType } from '@client/new/common/types';
import { toThumbnailUrl } from '../collection/mappers';
import {
  CollectionStats,
  CollectionsModel,
  LiteCollection,
  LiteCollectionType,
  RawCollection,
} from './collections.domain';
import { ArrayItem } from '@client/new/helpers/types';
import { getSalesPageUrl } from '@client/new/helpers/landingPages';

const getHasOngoingChallenges = (c: RawCollection): boolean => {
  return (c.challenges || []).some((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });
};

const getOngoingChallengeDeepLinkURL = (c: RawCollection): string | null => {
  const challenge = (c.challenges || []).find((ch) => {
    if (!ch) return false;
    const end = new Date(ch.completionEnd);
    return end.getTime() > Date.now();
  });

  return challenge?.deepLink || null;
};

export const rawDataToLiteCollection = (
  collections: Array<RawCollection>,
  customURL: string | null,
  profileDeepLink: string | null,
): Array<LiteCollection> =>
  collections.map((c): LiteCollection => {
    const ws = (c.workouts || []).filter(
      (w) => w !== null && w !== undefined,
    ) as WorkoutTiny[];
    return {
      title: c.name || '',
      thumbnailUrl: toThumbnailUrl(c.mediaCover),
      programId: c.id,
      ...toCollectionStats(ws),
      type: toLiteCollectionType(c),
      isLocked: !!c.isLocked,
      salesPageUrl: getSalesPageUrl({
        customURL: customURL || '',
        id: c.id,
        name: c.name || '',
      }),
      hasChallenges: (c.challenges || []).length > 0,
      hasOngoingChallenges: getHasOngoingChallenges(c),
      isVisible: c.isVisible || false,
      customURL: customURL || '',
      profileDeepLink: profileDeepLink || '',
      index: c.index ?? undefined,
      deeplinkUrl:
        c.exclusiveProgramMetadata?.deeplinkUrl || c.deeplinkUrl || undefined,
      accessType: c.accessType,
      challengeDeeplinkURL: getOngoingChallengeDeepLinkURL(c),
    };
  });

export const getAccessType = (type: SalesType): Array<AccessType> => {
  return type === 'otp'
    ? [AccessType.Exclusive, AccessType.PremiumExclusive]
    : [AccessType.Premium];
};

export type WorkoutTiny = NonNullable<
  ArrayItem<
    NonNullable<ArrayItem<GetCollectionsQuery['collections']>['workouts']>
  >
>;

export const toCollectionStats = (ws: WorkoutTiny[]): CollectionStats => {
  const ratings = ws
    .filter((w) => w.statsRatingAverage)
    .map((w) => w.statsRatingAverage) as number[];

  const totalRating = ratings.reduce((acc, c) => acc + c, 0);

  return {
    rating: ratings.length > 0 ? _.round(totalRating / ratings.length, 1) : 0,
    begins: ws.reduce((acc, c) => (acc += c.statsBegins || 0), 0),
    comments: ws.reduce((acc, c) => (acc += c.statsComments || 0), 0),
    completions: ws.reduce((acc, c) => (acc += c.statsCompletions || 0), 0),
  };
};

export const toLiteCollectionType = (
  p: Pick<
    ArrayItem<GetCollectionsQuery['collections']>,
    'archivedAt' | 'publishedAt'
  >,
): LiteCollectionType => {
  return p.archivedAt !== null && p.archivedAt !== undefined
    ? 'archived'
    : p.publishedAt !== null && p.publishedAt !== undefined
    ? 'published'
    : 'draft';
};

export const splitCollections = (
  data: LiteCollection[],
): CollectionsModel['collections'] => ({
  draft: data.filter((c) => c.type === 'draft'),
  published: data.filter((c) => c.type === 'published'),
  archived: data.filter((c) => c.type === 'archived'),
});
